import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { mapFieldToElement } from '../../form-generator/helpers/TypeToElemMapper';
import CountyMunicipalityDatalistFields from '../CountyMunicipality/CountyMunicipalityDatalistFields';
import { toast } from 'sonner';

const PROVIDER_SERVICE_OPTIONS = ['Water', 'Sewer', 'Storm Water', 'Solid Waste', 'Utility Other Services'];
const DEPARTMENT_OPTIONS = ['Building', 'Code'];
const HOW_TO_PAY_OPTIONS = ['Check', 'Online', 'Phone'];

const emptyFormValues = {
  providerName: '',
  mailPaymentTo: '',
  howToPay: [],
  makeCheckPayableTo: '',
  payOnlineUrl: '',
  city: '',
  state: '',
  department: [],
  contactNumbers: [],
  contactEmails: []
};

const toArray = value => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const normalizeStringArray = value => {
  if (!Array.isArray(value)) return [];
  return value.map(v => String(v).trim()).filter(Boolean);
};

const isValidEmail = s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());

const trimValue = value => String(value ?? '').trim();

const validateProviderHowToPay = v => {
  const howToPay = normalizeStringArray(v.howToPay);
  const hasCheck = howToPay.includes('Check');

  const rules = [
    [hasCheck && !trimValue(v.mailPaymentTo), 'mailPaymentTo', 'Mail Payment To is required when Check is selected in How to Pay.'],
    [hasCheck && !trimValue(v.makeCheckPayableTo), 'makeCheckPayableTo', 'Make Check Payable To is required when Check is selected in How to Pay.'],
    [howToPay.includes('Online') && !trimValue(v.payOnlineUrl), 'payOnlineUrl', 'Pay Online URL is required when Online is selected in How to Pay.'],
    [howToPay.includes('Phone') && !normalizeStringArray(v.contactNumbers).length, 'contactNumbers', 'At least one contact number is required when Phone is selected in How to Pay.']
  ];

  const failed = rules.filter(([condition]) => condition);
  const errors = Object.fromEntries(failed.map(([, field, message]) => [field, message]));

  return { errors, firstError: failed[0]?.[2] || null };
};

const getFieldValidationError = (field, v, activeTab) => {
  if (field.type === 'county-municipality-datalist') {
    const msgs = [];
    const county = String(v.state ?? '').trim();
    const city = String(v.city ?? '').trim();
    if (city && !county && activeTab === 'Provider') {
      msgs.push('County is required when city is selected.');
    }
    return msgs.length ? msgs.join(' ') : null;
  }

  if (field.type === 'tags-input') {
    const tags = normalizeStringArray(v[field.id]);
    const label = field.label || 'This field';
    if (field.id === 'contactNumbers' || field.id === 'contactEmails') {
      const numbers = normalizeStringArray(v.contactNumbers);
      const emails = normalizeStringArray(v.contactEmails);
      if (numbers.length === 0 && emails.length === 0) {
        return 'At least one contact number or email is required';
      }
      if (field.id === 'contactEmails' && tags.length > 0) {
        const bad = tags.filter(email => !isValidEmail(email));
        if (bad.length === 1) return `Invalid email address: ${bad[0]}`;
        if (bad.length > 1) return `Invalid email addresses: ${bad.join(', ')}`;
      }
      return null;
    }
    if (field.required && tags.length === 0) return `${label} is required`;
    return null;
  }

  if (!field.required) return null;

  const label = field.label || 'This field';
  const value = v[field.id];
  if (Array.isArray(value)) {
    return normalizeStringArray(value).length === 0 ? `${label} is required` : null;
  }
  return String(value ?? '').trim() ? null : `${label} is required`;
};

const initialValuesContentKey = iv => {
  const department = toArray(iv?.department);
  const contactNumbers = toArray(iv?.contactNumbers);
  const contactEmails = toArray(iv?.contactEmails);
  const howToPay = toArray(iv?.howToPay);
  return JSON.stringify({
    providerName: iv?.providerName ?? '',
    mailPaymentTo: iv?.mailPaymentTo ?? '',
    howToPay,
    makeCheckPayableTo: iv?.makeCheckPayableTo ?? '',
    payOnlineUrl: iv?.payOnlineUrl ?? '',
    city: iv?.city ?? '',
    state: iv?.state ?? '',
    department,
    contactNumbers,
    contactEmails
  });
};

const getFieldsByTab = activeTab => {
  const countyField = {
    id: 'countyAndMunicipality',
    variant: activeTab === 'County Department' ? 'countyOnly' : 'countyAndMunicipality',
    type: 'county-municipality-datalist',
    required: activeTab !== 'Provider',
    countyRequired: activeTab !== 'Provider',
    cityRequired: activeTab === 'City Department',
    creatable: true,
    layout: 'row',
    columnOrder: 'cityFirst'
  };

  const tailFields = () => [
    {
      id: 'department',
      label: activeTab === 'Provider' ? 'Service' : 'Department',
      type: 'multi-select',
      required: true,
      options: activeTab === 'Provider' ? PROVIDER_SERVICE_OPTIONS : DEPARTMENT_OPTIONS,
      placeholder: activeTab === 'Provider' ? 'Select services...' : 'Select departments...',
      containerClassName: 'mx-0 px-0'
    },
    {
      id: 'contactNumbers',
      label: 'Contact Numbers',
      type: 'tags-input',

      placeholder: 'Enter contact numbers...',
      size: 'lg'
    },
    {
      id: 'contactEmails',
      label: 'Contact Emails',
      type: 'tags-input',

      placeholder: 'Enter contact emails...',
      size: 'lg',
      inputType: 'email'
    }
  ];

  if (activeTab === 'Provider') {
    return [
      [
        {
          id: 'providerName',
          label: 'Provider Name',
          type: 'text',
          required: true,
          placeholder: 'Enter provider name...',
          size: 'lg'
        },
        {
          id: 'mailPaymentTo',
          label: 'Mail Payment To',
          type: 'text',
          required: false,
          placeholder: 'Enter mail payment to...',
          size: 'lg'
        }
      ],
      [
        {
          id: 'howToPay',
          label: 'How to Pay',
          type: 'multi-select',
          required: false,
          options: HOW_TO_PAY_OPTIONS,
          placeholder: 'Select payment methods...',
          containerClassName: 'mx-0 px-0'
        }
      ],
      [
        {
          id: 'makeCheckPayableTo',
          label: 'Make Check Payable To',
          type: 'text',
          required: false,
          placeholder: 'Enter make check payable to...',
          size: 'lg'
        },
        {
          id: 'payOnlineUrl',
          label: 'Pay Online URL',
          type: 'text',
          required: false,
          placeholder: 'Enter pay online URL...',
          size: 'lg'
        }
      ],
      [countyField],
      ...tailFields().map(field => [field])
    ];
  }

  if (activeTab === 'City Department') {
    return [[countyField], ...tailFields().map(field => [field])];
  }

  return [[countyField], ...tailFields().map(field => [field])];
};

const AddEditCityContactsForm = ({ activeTab, type = 'add', initialValues = {}, onSubmit, loading = false }) => {
  const [formValues, setFormValues] = useState(emptyFormValues);
  const [errors, setErrors] = useState({});
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const fieldRows = useMemo(() => getFieldsByTab(activeTab), [activeTab]);
  const fields = useMemo(() => fieldRows.flat(), [fieldRows]);

  const initialKey = initialValuesContentKey(initialValues);

  useEffect(() => {
    setFormValues({
      ...emptyFormValues,
      ...initialValues,
      department: toArray(initialValues.department),
      contactNumbers: toArray(initialValues.contactNumbers),
      contactEmails: toArray(initialValues.contactEmails),
      howToPay: toArray(initialValues.howToPay)
    });
    setErrors({});
    setFormInstanceKey(k => k + 1);
  }, [initialKey, activeTab]);

  const setFieldValue = (fieldId, value) => {
    setFormValues(prev => {
      const next = { ...prev, [fieldId]: value };
      if (fieldId === 'providerName' && type === 'add' && activeTab === 'Provider') {
        next.makeCheckPayableTo = value;
      }
      return next;
    });
    setErrors(prev => {
      const next = { ...prev, [fieldId]: '' };
      if (fieldId === 'contactNumbers' || fieldId === 'contactEmails') {
        next.contactNumbers = '';
        next.contactEmails = '';
      }
      if (fieldId === 'howToPay') {
        next.mailPaymentTo = '';
        next.makeCheckPayableTo = '';
        next.payOnlineUrl = '';
        next.contactNumbers = '';
      }
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    let firstError = null;
    const groupMsg = 'At least one contact number or email is required';
    for (const field of fields) {
      const msg = getFieldValidationError(field, formValues, activeTab);
      if (!msg) continue;
      if (msg === groupMsg && field.id === 'contactEmails') {
        if (firstError == null) firstError = msg;
        continue;
      }
      nextErrors[field.id] = msg;
      if (firstError == null) firstError = msg;
    }

    if (activeTab === 'Provider') {
      const { errors: howToPayErrors, firstError: howToPayFirstError } =
        validateProviderHowToPay(formValues);
      Object.assign(nextErrors, howToPayErrors);
      if (!firstError && howToPayFirstError) {
        firstError = howToPayFirstError;
      }
    }

    setErrors(nextErrors);
    return firstError;
  };

  const handleSubmit = event => {
    event.preventDefault();
    const firstError = validate();
    if (firstError) {
      toast.error(firstError);
      return;
    }
    onSubmit(formValues);
  };

  const renderMapperElement = field => {
    const id = field.id;
    const title = field.label;

    switch (field.type) {
      case 'county-municipality-datalist':
        return (
          <CountyMunicipalityDatalistFields
            {...field}
            initialCounty={formValues.state}
            initialCity={formValues.city}
            onFormChange={({ county, city: cityVal }) => {
              setFormValues(prev => ({ ...prev, state: county, city: cityVal }));
              setErrors(prev => ({ ...prev, [field.id]: '' }));
            }}
          />
        );
      case 'text':
        return mapFieldToElement({
          ...field,
          type: 'text',
          id,
          title,
          hint: field.placeholder,
          required: field.required,
          value: formValues[field.id],
          onFormChange: e => setFieldValue(field.id, e.target.value)
        });
      case 'multi-select':
        return mapFieldToElement({
          ...field,
          type: 'multi-select',
          id,
          label: '',
          title,
          placeholder: field.placeholder,
          options: field.options.map(option => ({ label: option, value: option })),
          selectedOptions: formValues[field.id],
          onChange: selected =>
            setFieldValue(
              field.id,
              selected.map(option => option.value)
            ),
          menuPlacement: 'bottom'
        });
      case 'tags-input':
        return mapFieldToElement({
          ...field,
          type: 'tags-input',
          id,
          title,
          placeholder: field.placeholder,
          preValue: formValues[field.id],
          onFormChange: e => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFieldValue(field.id, Array.isArray(parsed) ? parsed : []);
            } catch {
              setFieldValue(field.id, []);
            }
          }
        });
      default:
        return null;
    }
  };

  const renderFieldCol = field => {
    const inner = renderMapperElement(field);
    if (!inner) {
      return null;
    }

    return (
      <Col key={field.id} xs={12} className="mb-0">
        <Form.Group>
          {field.label && (
            <Form.Label>
              {field.label}
              {field.required ? <span className="text-danger">*</span> : null}
            </Form.Label>
          )}
          {inner}
          {errors[field.id] ? <div className="text-danger small mt-1">{errors[field.id]}</div> : null}
        </Form.Group>
      </Col>
    );
  };

  return (
    <Form key={formInstanceKey} onSubmit={handleSubmit} className="m-2">
      {fieldRows.map((row, rowIndex) => (
        <Row key={rowIndex}>{row.map(field => renderFieldCol(field))}</Row>
      ))}

      <div className="d-flex justify-content-end">
        <Button type="submit" variant="success" disabled={loading}>
          {loading ? `${type === 'edit' ? 'Updating' : 'Saving'}...` : type === 'edit' ? 'Update' : 'Add'}
        </Button>
      </div>
    </Form>
  );
};

export default AddEditCityContactsForm;
